import React, { useState } from 'react';
import { FiCheckCircle, FiZap } from 'react-icons/fi';
import { openBillingPortal } from '../billingApi';
import { useEntitlements } from '../contexts/EntitlementsContext';
import { useLanguage } from '../contexts/LanguageContext';
import { hasPaidAccess } from '../entitlements';
import PlanComparison from './PlanComparison';
import ProCheckoutButton from './ProCheckoutButton';
import WhishPaymentStatus from './WhishPaymentStatus';

function SubscriptionPanel({ user, billingResult = '', paymentId = '', whishAttempt = '' }) {
  const { entitlements } = useEntitlements();
  const { language, t } = useLanguage();
  const tb = (key) => t(`profilePage.billing.${key}`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const paid = hasPaidAccess(entitlements);
  const whish = entitlements?.billing?.provider === 'whish';
  const manageBilling = !whish && (paid || ['past_due', 'unpaid', 'incomplete', 'paused'].includes(entitlements?.subscriptionStatus));
  const trialEndsAt = Number(entitlements?.scout?.trialEndsAt || 0);
  const trialActive = Boolean(entitlements?.scout?.trialActive && trialEndsAt > Date.now());
  const currentPeriodEnd = Number(entitlements?.billing?.currentPeriodEnd || 0);
  const formatDate = (timestamp) => new Intl.DateTimeFormat(language === 'ar' ? 'ar-LB' : 'en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(timestamp));
  const planLabel = paid && entitlements?.billing?.cancelAtPeriodEnd && currentPeriodEnd
    ? tb('accessEnds').replace('{date}', formatDate(currentPeriodEnd))
    : trialActive
      ? tb('trialEnds').replace('{date}', formatDate(trialEndsAt))
      : paid
        ? tb('proPlan')
        : tb('freePlan');

  const openBilling = async () => {
    if (!user || busy) return;
    setBusy(true);
    setError('');
    try {
      await openBillingPortal(user);
    } catch (requestError) {
      setError(requestError.message);
      setBusy(false);
    }
  };

  return (
    <section className="js-profile-card js-subscription-panel" aria-labelledby="subscription-title">
      <div className="js-profile-card__head">
        <h2 id="subscription-title">{paid ? tb('title') : tb('upgradeTitle')}</h2>
        <FiZap aria-hidden="true" />
      </div>
      <p className="js-subscription-panel__description">{paid ? planLabel : tb('upgradeDescription')}</p>
      {whish && <p>{language === 'ar' ? 'الدفع عبر Whish لا يتجدد تلقائياً. يتاح التجديد قبل 7 أيام من انتهاء الاشتراك.' : 'Whish does not auto-renew. Renewal opens seven days before your access ends.'}</p>}
      <div className="js-subscription-panel__actions">
        {manageBilling ? <button type="button" onClick={openBilling} disabled={busy}>
          {busy ? tb('opening') : tb('manage')}
        </button> : <ProCheckoutButton user={user} whishOnly={whish && paid}
          disabled={whish && paid && !entitlements?.billing?.renewalAvailable}>
          {whish ? (language === 'ar' ? 'تجديد Pro · 20 دولاراً' : 'Renew Pro · $20') : tb('upgrade')}
        </ProCheckoutButton>}
        {!paid && <PlanComparison user={user} />}
      </div>
      {billingResult === 'whish' && paymentId && <WhishPaymentStatus user={user} paymentId={paymentId} failedAttempt={whishAttempt === 'failed'} />}
      {billingResult === 'success' && paid && (
        <p className="js-subscription-panel__success"><FiCheckCircle /> {tb('success')}</p>
      )}
      {billingResult === 'cancelled' && (
        <p className="js-subscription-panel__notice">{tb('cancelled')}</p>
      )}
      {error && <p className="js-subscription-panel__error" role="alert">{error}</p>}
    </section>
  );
}

export default SubscriptionPanel;
